import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import SEO from './SEO';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { resetPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (error) {
      setError(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Forgot Password"
        description="Reset your SharpeMetrix account password."
        url="/forgot-password"
      />
      <div className="min-h-screen flex items-center justify-center bg-bg-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-sm">
            <img
              className="mx-auto h-10 w-auto"
              src="/logo_wordmark.svg"
              alt="SharpeMetrix"
            />
            <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-text-100">
              Reset your password
            </h2>
            <p className="mt-2 text-center text-sm text-text-200">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          {success ? (
            <div className="rounded-md bg-green-900/50 p-4">
              <div className="text-sm text-green-300 text-center">
                <p className="font-medium">Check your email</p>
                <p className="mt-1">We've sent a password reset link to <strong>{email}</strong>.</p>
                <p className="mt-1">If you don't see it, check your spam folder.</p>
              </div>
              <div className="mt-4 text-center">
                <a href="/login" className="text-sm font-semibold text-primary-100 hover:text-primary-200">
                  Back to sign in
                </a>
              </div>
            </div>
          ) : (
            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email-address" className="sr-only">
                  Email address
                </label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none relative block w-full px-3 py-2 border border-bg-300 placeholder-text-200 text-text-100 rounded-md focus:outline-none focus:ring-primary-100 focus:border-primary-100 focus:z-10 sm:text-sm bg-bg-200"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              {error && (
                <div className="text-red-500 text-sm text-center">{error}</div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-text-100 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-100"
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                </button>
              </div>

              <p className="text-center text-sm text-text-200">
                Remember your password?{' '}
                <a href="/login" className="font-semibold leading-6 text-primary-100 hover:text-primary-200">
                  Sign in
                </a>
              </p>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
